import { useCallback, useState } from "react";
import { api } from "../../utils/api";
import {
  AgentItem,
  AgentListResponse,
  AgentFilterOption,
  AgentDeployResponse,
  AgentDeployCancelResponse,
  AgentDeleteResponse,
} from "../../types/agent";

interface AgentState {
  agentList: AgentItem[];
  categories: string[];
  totalCount: number;
  currentPage: number;
}

export const useAgentManagement = () => {
  const [state, setState] = useState<AgentState>({
    agentList: [],
    categories: [],
    totalCount: 0,
    currentPage: 1,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isDeployLoading, setIsDeployLoading] = useState(false);
  const [isDeployCancelLoading, setIsDeployCancelLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployCancelError, setDeployCancelError] = useState<string | null>(
    null
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleError = (error: any) => {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "알 수 없는 오류가 발생했습니다.";

    setError(errorMessage);
  };

  // 카테고리 목록 조회
  const fetchCategories = useCallback(async () => {
    setIsCategoriesLoading(true);
    setError(null);

    try {
      const response = await api.get<string[]>(`/admin/v1/agent/categories`);

      // API 응답이 배열 형태로 직접 오는 경우
      if (Array.isArray(response.data)) {
        setState((prev) => ({
          ...prev,
          categories: response.data as string[],
        }));
      } else {
        throw new Error("카테고리 목록을 가져올 수 없습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsCategoriesLoading(false);
    }
  }, []);

  // 에이전트 리스트 조회
  const fetchAgentList = useCallback(
    async (page: number = 1, category: string = "") => {
      setIsLoading(true);
      setError(null);

      try {
        const categoryParam = category
          ? `?ct=${encodeURIComponent(category)}`
          : "";
        const pageParam = category ? `&page=${page}` : `?page=${page}`;

        const response = await api.get<AgentListResponse>(
          `/admin/v1/agent/list${categoryParam}${
            category ? pageParam : pageParam
          }`
        );

        if (response.data.status) {
          const currentDataLength = response.data.data.length;
          const pageSize = 15;
          
          // 빈 페이지 처리
          if (currentDataLength === 0 && page > 1) {
            const actualTotalCount = (page - 1) * pageSize;
            setState((prev) => ({
              ...prev,
              totalCount: actualTotalCount,
              currentPage: page - 1, 
            }));
            return;
          }
          
          let estimatedTotalCount: number;
          
          if (currentDataLength === pageSize) {

            estimatedTotalCount = page * pageSize + 1; 
          } else {
            estimatedTotalCount = (page - 1) * pageSize + currentDataLength;
          }

          setState((prev) => ({
            ...prev,
            agentList: response.data.data,
            totalCount: estimatedTotalCount,
            currentPage: page,
          }));
        } else {
          throw new Error("에이전트 목록을 가져올 수 없습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // 에이전트 검색
  const fetchAgentSearch = useCallback(
    async (query: string, category: string = "", page: number = 1) => {
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append("q", query);
        if (category) params.append("ct", category);
        params.append("page", page.toString());

        const response = await api.get<AgentListResponse>(
          `/admin/v1/agent/search?${params.toString()}`
        );

        if (response.data.status) {
          setState((prev) => ({
            ...prev,
            agentList: response.data.data,
            totalCount: response.data.total_count || response.data.data.length,
            currentPage: page,
          }));
        } else {
          throw new Error("검색 결과를 가져올 수 없습니다.");
        }
      } catch (error) {
        handleError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const resetState = useCallback(() => {
    setState({
      agentList: [],
      categories: [],
      totalCount: 0,
      currentPage: 1,
    });
    setError(null);
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  }, []);

  const categoryOptions: AgentFilterOption[] = [
    { value: "", label: "전체 카테고리" },
    ...state.categories.map((category) => ({
      value: category,
      label: category,
    })),
  ];

  const deployAgent = useCallback(async (id: string) => {
    setIsDeployLoading(true);
    setDeployError(null);

    try {
      const response = await api.post<AgentDeployResponse>(
        `/admin/v1/agent/deploy`,
        {
          id,
        }
      );

      if (response.data.status) {
        return true;
      } else {
        throw new Error("에이전트 배포에 실패했습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsDeployLoading(false);
    }
  }, []);

  const deployCancelAgent = useCallback(async (id: string) => {
    setIsDeployCancelLoading(true);
    setDeployCancelError(null);

    try {
      const response = await api.post<AgentDeployCancelResponse>(
        `/admin/v1/agent/deploy/cancel`,
        {
          id,
        }
      );

      if (response.data.status) {
        return true;
      } else {
        throw new Error("에이전트 배포 취소에 실패했습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsDeployCancelLoading(false);
    }
  }, []);

  const deleteAgent = useCallback(async (id: string) => {
    setIsDeleteLoading(true);
    setDeleteError(null);

    try {
      const response = await api.post<AgentDeleteResponse>(
        `/admin/v1/agent/delete`,
        {
          id,
        }
      );

      if (response.data.status) {
        return true;
      } else {
        throw new Error("에이전트 삭제에 실패했습니다.");
      }
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setIsDeleteLoading(false);
    }
  }, []);

  return {
    agentList: state.agentList,
    categories: state.categories,
    totalCount: state.totalCount,
    currentPage: state.currentPage,
    categoryOptions,

    isLoading,
    isCategoriesLoading,
    error,

    isDeployLoading,
    isDeployCancelLoading,
    isDeleteLoading,
    deployError,
    deployCancelError,
    deleteError,

    fetchCategories,
    fetchAgentList,
    fetchAgentSearch,

    deployAgent,
    deployCancelAgent,
    deleteAgent,

    resetState,
    setCurrentPage,

    totalPages: Math.ceil(state.totalCount / 15),
    hasAgents: state.agentList.length > 0,
    isEmpty: state.agentList.length === 0 && !isLoading,
  };
};
